// HTTP klient vlastního vyhledávání (issue #67, epic #62).
// Kontrakt: `search/README.md`, koncepce `docs/ops/vyhledavani-koncepce.md`
// §API kontrakt. Živé API: https://hledani.jipos.cz
//
// ⚠️ API je **CORS-simple** a musí takové zůstat: prosté GETy, ŽÁDNÉ vlastní
// hlavičky, `credentials: 'omit'`. Preflight `OPTIONS` server záměrně vrací 404,
// aby při psaní do našeptávače odpadl RTT — jakmile sem někdo přidá
// `Content-Type: application/json` nebo vlastní hlavičku, request se rozbije.
// Události se posílají `sendBeacon`em jako `text/plain` (taky simple).
//
// ZÁSADA: **tenhle modul nikdy nehází.** Cokoli mimo 2xx, prázdné tělo,
// nevalidní JSON, timeout nebo odpověď bez `total` = `null` → volající
// degraduje na nativní Shoptet výsledky (koncepce §Fallback, úrovně Ú1/Ú2).

import { getConfig, getIndexConfig, getRetailConfig, tripBreaker, takeBreakerProbe } from './searchConfig.js';
import { localizeUrls, pageOrigin } from './originGuard.js';

// Rozpočet latence z koncepce: p50 ~30–40 ms, p95 ~60–70 ms. Timeouty jsou
// nad p95 s velkou rezervou — po nich už je nativní výsledek lepší než čekání.
const TIMEOUT = { autocomplete: 900, search: 1200, browse: 1200, popular: 1500, recommend: 2000, specs: 2000, history: 2000, sister: 1500, reviews: 2000 };

// Timer po doručení HLAVIČEK odpověď neruší: tělo (~11 kB) je pak otázka
// milisekund a abort by zahodil hotovou odpověď. Sonda v2 švu /vyhledavani/
// (ops/sweeps/2026-09-15-vyhledavani-vypadek-a-sev/) ukázala, že na mobilu
// s pomalým CPU se odpověď (síť 180 ms) dostane na hlavní vlákno až po 2s
// bootovém tasku — a abort vystřelil 40–90 ms po hlavičkách. Skutečný
// výpadek hlavičky nedá, takže se abort nezdrží ani o milisekundu; zaseklé
// tělo kryje tvrdý strop `timeout + BODY_GRACE_MS`.
const BODY_GRACE_MS = 1000;

/** Důvody neúspěchu (`diag.reason`) — sloupec `reason` v `shared.search_events`. */
export const REASONS = ['timeout', 'net', 'json', 'invalid']; // + 'http_<status>'

// LRU stačí malá — uživatel se v rámci jednoho hledání vrací na pár dotazů
// (mazání znaků, přepínání řazení, tlačítko zpět).
const CACHE_MAX = 30;
const cache = new Map();

function cacheGet(key) {
  if (!cache.has(key)) return undefined;
  const value = cache.get(key);
  cache.delete(key); // re-insert = posun na konec (nejčerstvější)
  cache.set(key, value);
  return value;
}

function cacheSet(key, value) {
  if (cache.has(key)) cache.delete(key);
  cache.set(key, value);
  if (cache.size > CACHE_MAX) cache.delete(cache.keys().next().value);
}

/** Vyprázdnění cache — po změně cenové hladiny (přihlášení) by lhala. */
export function clearCache() {
  cache.clear();
}

function url(cfg, path, params) {
  const u = new URL(cfg.api + '/v1/' + cfg.tenant + '/' + path);
  Object.keys(params || {}).forEach(function (key) {
    const value = params[key];
    if (value === null || value === undefined || value === '') return;
    u.searchParams.set(key, value);
  });
  return u.toString();
}

/**
 * Odkazy s cizím hostem → relativní cesta (originGuard.js, incident 2026-09-21:
 * našeptávač ostré posílal zákazníky na dev). Běží nad každou odpovědí dřív,
 * než ji kdokoli uvidí nebo uloží do cache; nález = jedna `[bs]` chyba.
 */
function guardOrigins(data, path) {
  const r = localizeUrls(data, pageOrigin());
  if (r.count) {
    console.error(
      '[bs] searchApi /' + path + ': ' + r.count + 'x odkaz s cizim hostem (' + r.hosts.join(', ') + ') prepsan na relativni',
    );
  }
  return data;
}

function isAbort(e) {
  return !!e && e.name === 'AbortError';
}

/**
 * Jeden GET. Vrací data, nebo `null` — NIKDY nehází a nikdy nevrací částečný
 * tvar. `timeout` v ms, `AbortController` ruší i visící spojení. `config` přebije
 * výchozí `getConfig()` (dotaz na běžné ceny pro velkoobchod, `wholesaleSpecs`).
 * `diag` (volitelný objekt) dostane `reason` (`timeout | net | http_<status> |
 * json | invalid`), `status` a `tookMs` — analytika z něj skládá důvod fallbacku.
 */
async function get(path, params, timeout, config, diag) {
  const cfg = config || getConfig();
  if (!cfg) return null;
  const d = diag || {};
  const started = Date.now();
  const controller = new AbortController();
  let headersAt = 0;
  let timer = setTimeout(function fire() {
    if (headersAt) {
      // hlavičky už jsou na hlavním vlákně → tělo doběhne, jen tvrdý strop
      timer = setTimeout(function () {
        controller.abort();
      }, BODY_GRACE_MS);
      return;
    }
    controller.abort();
  }, timeout);
  const init = {
    // ŽÁDNÉ `headers` — viz hlavička souboru (CORS-simple)
    method: 'GET',
    credentials: 'omit',
    mode: 'cors',
    signal: controller.signal,
  };
  // po obnově z breakeru jde první GET mimo HTTP cache (searchConfig.js)
  if (takeBreakerProbe()) init.cache = 'no-store';

  try {
    let res;
    try {
      res = await fetch(url(cfg, path, params), init);
    } catch (e) {
      d.reason = isAbort(e) ? 'timeout' : 'net';
      return null;
    }
    headersAt = Date.now();
    d.status = res.status;
    if (!res.ok) {
      // 5xx = backend leží → shodíme vrstvu na BREAKER_TTL_MS, ať uživatel
      // nečeká na timeout u každého dalšího dotazu (koncepce §Ú0).
      if (res.status >= 500) tripBreaker();
      d.reason = 'http_' + res.status;
      return null;
    }
    let data;
    try {
      data = await res.json();
    } catch (e) {
      // `json` jen pro chybu parsování; spojení spadlé až po hlavičkách 200
      // odmítne `res.json()` síťovým TypeErrorem = `net` (prefetch totéž hlásí `net`)
      d.reason = isAbort(e) ? 'timeout' : e && e.name === 'SyntaxError' ? 'json' : 'net';
      return null;
    }
    if (!data || typeof data !== 'object') {
      d.reason = 'invalid';
      return null;
    }
    return guardOrigins(data, path);
  } finally {
    clearTimeout(timer);
    d.tookMs = Date.now() - started;
  }
}

/** Klíč cache: dotaz + všechny parametry, které mění odpověď. */
function key(path, params) {
  return path + '|' + JSON.stringify(params);
}

async function cachedGet(path, params, timeout, config, diag) {
  const k = key(path, params);
  const hit = cacheGet(k);
  if (hit !== undefined) return hit;
  const data = await get(path, params, timeout, config, diag);
  if (data) cacheSet(k, data); // chyby se necachují — příští pokus má šanci
  return data;
}

function searchParams(cfg, q, opts) {
  const o = opts || {};
  return {
    q: q,
    offset: o.offset || 0,
    limit: o.limit || 12,
    order: o.order || '',
    pl: cfg.priceListId,
  };
}

/**
 * URL dotazu `/search` přesně tak, jak ji posílá `search()` — zdroj pravdy pro
 * prefetch snippet v hlavičce (shoptet-includes/common-header.html, markery
 * `bs-search-prefetch:start|end`), který ji nese jako ručně minifikovanou kopii;
 * shodu hlídá `tools/unit/search-prefetch.test.mjs`.
 */
export function searchUrl(cfg, q, opts) {
  return url(cfg, 'search', searchParams(cfg, q, opts));
}

// --------------------------------------------------------------- prefetch
//
// Stránka výsledků: inline snippet v hlavičce vystřelí GET `/search` už při
// parsování HTML (před 11 synchronními skripty Shoptetu a bundlem) a vystaví
// `window.__bsSearchPrefetch = {url, ctrl, promise, headersAt?, result?, error?}`
// (`headersAt` = čas doručení hlaviček, `result = {status, text}` až po dočtení
// těla). Bundle při bootu odpověď převezme PROMISEM —
// HTTP cache nestačí: i cached odpověď se na hlavní vlákno doručí až jako
// další task po bootu a timer ji předběhne (sonda v2, buňka „obojí": cache hit
// 0 ms, fallback 10/10). Převezme se jen při shodě CELÉ URL (dotaz, limit,
// řazení, ceník, tenant) — cokoli jiného prefetch zahodí a jede dnešní fetch.

function readPrefetch() {
  try {
    const pf = window.__bsSearchPrefetch;
    if (!pf || typeof pf !== 'object' || !pf.promise) return null;
    window.__bsSearchPrefetch = null; // jednorázově
    return pf;
  } catch (e) {
    return null;
  }
}

function abortPrefetch(pf) {
  try {
    if (pf && pf.ctrl && !pf.result && !pf.error) pf.ctrl.abort();
  } catch (e) {
    /* nic */
  }
}

/** Zahodí visící prefetch (Ú0 — vrstva se nespustí, request ať neběží naprázdno). */
export function discardPrefetch() {
  abortPrefetch(readPrefetch());
}

function parsePrefetched(result, d) {
  if (!result || typeof result.status !== 'number') {
    d.reason = 'invalid';
    return null;
  }
  d.status = result.status;
  if (result.status < 200 || result.status >= 300) {
    if (result.status >= 500) tripBreaker();
    d.reason = 'http_' + result.status;
    return null;
  }
  let data;
  try {
    data = JSON.parse(result.text);
  } catch (e) {
    d.reason = 'json';
    return null;
  }
  if (!data || typeof data !== 'object') {
    d.reason = 'invalid';
    return null;
  }
  return guardOrigins(data, 'search');
}

/**
 * Převezme prefetch pro `target` URL. Vrací `undefined` = žádný použitelný
 * prefetch (volající jede dnešní cestou; prefetch zamítnutý ještě před bootem
 * dostane touhle cestou jeden nový pokus), jinak promise dat nebo `null`.
 */
function takePrefetch(target, timeout, d) {
  const pf = readPrefetch();
  if (!pf) return undefined;
  if (pf.url !== target) {
    abortPrefetch(pf);
    return undefined;
  }
  if (pf.error) return undefined;
  d.prefetch = pf.result ? 'ready' : 'pending';
  if (pf.result) return Promise.resolve(parsePrefetched(pf.result, d));
  return waitPrefetch(pf, timeout, d);
}

async function waitPrefetch(pf, timeout, d) {
  const started = Date.now();
  // Timer nad visícím prefetchem ruší JEHO controller — stejný rozpočet jako
  // dnešní fetch, počítaný od bootu (co visí déle, visí i novému requestu).
  // Stejná ochrana těla jako v `get()`: `result` snippet vystaví až po dočtení
  // těla, takže doručení hlaviček nese vlastní `headersAt` — s ním timer odpověď
  // neruší a dá jí `BODY_GRACE_MS` (Codex review PR #377: tělo za 1 500 ms
  // obyčejný fetch přežil, prefetch ne).
  let timer = setTimeout(function fire() {
    if (pf.headersAt) {
      timer = setTimeout(function () {
        abortPrefetch(pf);
      }, BODY_GRACE_MS);
      return;
    }
    abortPrefetch(pf);
  }, timeout);
  try {
    const result = await pf.promise;
    return parsePrefetched(result, d);
  } catch (e) {
    d.reason = isAbort(e) ? 'timeout' : 'net';
    return null;
  } finally {
    clearTimeout(timer);
    d.tookMs = Date.now() - started;
  }
}

/**
 * Výsledky hledání. Vrací syrovou odpověď API, nebo `null`.
 * Odpověď **bez `total`** je dle kontraktu nevalidní (server nesmí vracet 200
 * s částečnými daty) → taky `null`. `opts.diag` = objekt pro důvod neúspěchu
 * (viz `get()`; u prefetche navíc `prefetch: 'ready' | 'pending'`).
 */
export async function search(q, opts = {}) {
  if (!q) return null;
  const cfg = getConfig();
  if (!cfg) return null;

  const d = opts.diag || {};
  const params = searchParams(cfg, q, opts);
  let data;
  const pre = takePrefetch(url(cfg, 'search', params), TIMEOUT.search, d);
  if (pre !== undefined) {
    data = await pre;
    if (data) cacheSet(key('search', params), data); // zpět z detailu = z LRU
  } else {
    data = await cachedGet('search', params, TIMEOUT.search, undefined, d);
  }
  if (!data || typeof data.total !== 'number') {
    if (data) d.reason = 'invalid';
    return null;
  }
  return data;
}

/**
 * Procházení výprodeje (`/browse?scope=sale`, saleBrowse.js): karty a fasety
 * výřezu akční nabídky. Jen ceník 1 — přihlášený velkoobchod zůstává na
 * nativním výpisu (akce ceníku 1 pro něj neplatí, VO ceník akce nemá). Timeout
 * jako stránka výsledků: po něm je nativní výpis lepší než čekání. Odpověď bez
 * `total` = `null`.
 */
export async function browse(params, diag) {
  const cfg = getRetailConfig();
  if (!cfg) return null;
  const d = diag || {};
  const data = await cachedGet('browse', Object.assign({}, params, { pl: cfg.priceListId }), TIMEOUT.browse, cfg, d);
  if (!data || typeof data.total !== 'number') {
    if (data) d.reason = 'invalid';
    return null;
  }
  return data;
}

/** Našeptávač. `lp` = produktů, `lq` = položek obsahových sekcí. */
export async function autocomplete(q, opts = {}) {
  if (!q) return null;
  const cfg = getConfig();
  if (!cfg) return null;

  return cachedGet(
    'autocomplete',
    { q: q, lp: opts.products || 5, lq: opts.queries || 5, pl: cfg.priceListId },
    TIMEOUT.autocomplete,
  );
}

/**
 * Bestsellery. POZOR na jméno: `/popular` vrací **produkty** (`items[]`),
 * ne populární dotazy — ty index zatím nemá (#88).
 */
export async function popular(limit, order) {
  const cfg = getConfig();
  if (!cfg) return null;
  // `pl` = ceník zákazníka: karty přijdou v jeho ceně (velkoobchod 13/16/19)
  const params = { limit: limit || 8, pl: cfg.priceListId };
  // `seasonal` = sezónně vážená tržba (F4, #243) — homepage řada „Nejoblíbenější"
  if (order) params.order = order;
  return cachedGet('popular', params, TIMEOUT.popular);
}

/**
 * Pořadí kategorií dle sezónně vážené tržby (`/category-rank`, F4 #243) —
 * rozcestník homepage. `strategy !== 'revenue_seasonal'` = drž adminové pořadí.
 * Cenu nenese → i pro velkoobchod (`getIndexConfig`).
 */
export async function categoryRank() {
  const cfg = getIndexConfig();
  if (!cfg) return null;
  return cachedGet('category-rank', {}, TIMEOUT.popular, cfg);
}

/**
 * Doporučení (`/recommend`). Pro řadu „Podobné skladem" pod vyprodanými
 * výsledky (#284) se volá s `kind: 'search'` — server tam vrací JEN produkty
 * skladem (alternativy zdrojových kódů, pak listová kategorie) a bez fallbacku;
 * volající proto kreslí až při `strategy` `category_in_stock` nebo
 * `alternative_in_stock` (#290), jiná hodnota (starý API s řetězem fallbacků,
 * `empty`) = nic. Timeout jako u `/specs`: řada se
 * dokresluje do hotové stránky, nikdo na ni nečeká.
 *
 * @param {{kind?: string, codes?: string[], seen?: string[], limit?: number}} opts
 */
export async function recommend(opts) {
  const o = opts || {};
  const codes = Array.isArray(o.codes) ? o.codes.filter(Boolean) : [];
  if (!codes.length) return null;
  const cfg = getConfig();
  if (!cfg) return null;
  return cachedGet(
    'recommend',
    {
      kind: o.kind || 'detail',
      codes: codes.join(','),
      seen: Array.isArray(o.seen) ? o.seen.filter(Boolean).join(',') : '',
      limit: o.limit || 8,
      pl: cfg.priceListId,
    },
    TIMEOUT.recommend,
  );
}

/**
 * Parametry produktů po kódech — podklad pro spec chipy na kartě výpisu (#135),
 * samolepku Bestseller a konec akční ceny. Timeout je delší než u našeptávače
 * schválně: chipy nikdo neodklikává, dokreslí se do hotové stránky, takže je
 * lepší počkat než degradovat.
 *
 * Jede i pro velkoobchod (`getIndexConfig`): odpověď nese i `action` a `prices`
 * ceníku 1, takže kdo z ní kreslí akci, ptá se sám `getRetailConfig()`
 * (saleEnd.js, cardExtras.js `mountSaleEnd`, productDetail.js). `config` přebije výchozí
 * konfiguraci — běžné ceny pro velkoobchod jdou s `getWholesaleConfig()`
 * (bez stand-down guardu Luigi's Boxu). Volá se přes specsCache.js.
 *
 * `reviewCodes` = kódy z `codes`, jejichž položka má nést i `reviews`
 * (zvýrazněné recenze a hodnocení od kupujících, recenze U3) — detail se ptá
 * na svůj produkt v tomtéž GETu, výpis texty nechce.
 */
export async function specs(codes, config, reviewCodes) {
  const cfg = config || getIndexConfig();
  if (!cfg || !Array.isArray(codes) || !codes.length) return null;
  const params = { codes: codes.join(',') };
  if (Array.isArray(reviewCodes) && reviewCodes.length) params.reviews = reviewCodes.join(',');
  return cachedGet('specs', params, TIMEOUT.specs, cfg);
}

/**
 * Historie ceny produktu (řádek Omnibus + graf na detailu, priceHistory.js).
 * File-backed data na API (nejde přes Typesense) — jinak stejný kontrakt:
 * `{items: [{code, omnibus?, low30?, from, to, points}], total}`, nebo `null`.
 * Timeout jako u `/specs` — prvek se dokresluje do hotové stránky.
 */
export async function priceHistory(code) {
  if (!code) return null;
  return cachedGet('price-history', { code: code }, TIMEOUT.history);
}

/**
 * Souhrn hodnocení platforem (`/reviews/summary`, denní job recenzí na s1):
 * `{generated_at, platforms: [{id, metric, value, count, status, pool?}], total,
 * shop, missing}`. Patička (reviews.js) z řádků přepisuje zálohu z manifestu;
 * tenant bez souboru recenzí vrací prázdné `platforms` (= platí manifest).
 * Cenu nenese → i pro velkoobchod (`getIndexConfig`). `null` = vrstva neběží
 * nebo API nepřišlo.
 */
export async function reviewsSummary() {
  const cfg = getIndexConfig();
  if (!cfg) return null;
  return cachedGet('reviews/summary', {}, TIMEOUT.reviews, cfg);
}

/**
 * Citace hodnocení k typu stránky (`/reviews/context`): `page` = obecne |
 * doprava | reklamace | komunikace, nebo `kategorie` s GUID kategorie
 * (`category`, položky pak nesou `product: {code, name, path}`). `{page,
 * items}`, nebo `null`. Řádek citací se dokresluje do hotové patičky, timeout
 * jako u `/specs`.
 */
export async function reviewsContext(page, category, limit) {
  const cfg = getIndexConfig();
  if (!cfg || !page) return null;
  return cachedGet('reviews/context', { page: page, category: category || '', limit: limit || 3 }, TIMEOUT.reviews, cfg);
}

/**
 * Cesta téhož produktu na druhém shopu firmy (přepínač CZ ↔ SK, countrySwitch.js).
 * `/sister` jde na VLASTNÍ tenant: server najde kód v indexu sesterského tenantu
 * (`sister` v search/data/tenants.json) a vrátí jen cestu. Origin skládá šablona
 * z manifestu (sister.js), takže odpověď nenese pole `url` a originGuard ji nemění.
 * Cenu nenese → i pro velkoobchod (`getIndexConfig`). `null` = produkt na druhém
 * shopu není, API routu nezná (404 starší verze) nebo vrstva neběží.
 */
export async function sisterPath(codes) {
  const cfg = getIndexConfig();
  if (!cfg || !Array.isArray(codes) || !codes.length) return null;
  const data = await cachedGet('sister', { codes: codes.join(',') }, TIMEOUT.sister, cfg);
  const path = data && data.path;
  return typeof path === 'string' && path.charAt(0) === '/' ? path : null;
}

/**
 * Událost (dotaz, klik, konverze) — `sendBeacon` přežije odchod ze stránky.
 * `text/plain` je nutnost, ne pohodlí: `application/json` by vyvolal preflight,
 * který server nemá (viz hlavička souboru). Nikdy nehází. `config` = konfigurace,
 * pod kterou analytika události přijala (searchAnalytics.js) — po shození
 * breakeru je `getConfig()` `null`, ale fallback s `reason=http_5xx` odejít musí.
 */
export function sendEvent(payload, config) {
  const cfg = config || getConfig();
  if (!cfg || !navigator.sendBeacon) return false;
  try {
    const body = new Blob([JSON.stringify(payload)], { type: 'text/plain' });
    return navigator.sendBeacon(cfg.api + '/v1/' + cfg.tenant + '/events', body);
  } catch (e) {
    return false;
  }
}
